import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import { useState } from 'react'
import { HeaderUpdated } from '~widgets/header/components/header-updated'
import { Footer } from '~widgets/footer/components/footer'
import { Breadcrumbs } from '~widgets/breadcrumbs/breadcrumbs'
import { cn } from '~shared/utils/cn'
import { getCategoriesData } from '~shared/api/categories/getCategories'
import { getPopularPrograms } from '~shared/api/programs-popular/getPopularPrograms'
import { getFooterData } from '~shared/api/footer/getFooterData'
import Image from 'next/image'
import { LeadForm } from '~widgets/lead-form/components/lead-form'
import { ContactButtons } from '~pages/contact-page/contact-buttons'
import { PATHS } from '~shared/constants/paths'
import { YMaps, Map, Placemark } from '@pbe/react-yandex-maps'

type TContactPageProps = {
  categories: Awaited<ReturnType<typeof getCategoriesData>>
  footer: Awaited<ReturnType<typeof getFooterData>>
  popularPrograms: Awaited<ReturnType<typeof getPopularPrograms>>
}

const ALL_ITEMS = [
  {
    type: 'moscow' as const,
    city: 'Москва',
    address: 'Новочеремушкинская улица 71/32',
    addressExtra: 'Дербеневская набережная, д. 11',
    phones: ['+7(800)333-71-38', '+7(495)648-62-23'],
    email: 'info@mitm.institute',
    lat: 55.662151,
    lon: 37.562156,
    zoom: 17
  },
  {
    type: 'almaty' as const,
    city: 'Алматы',
    address:
      'г. Алматы, индекс 050000, Проспект Абая, 44А, 1 этаж, офис В2, PROMENADE, Бизнес-парк',
    addressExtra: null,
    phones: ['+7 771 7662224', '+7 (771) 766-22-20'],
    email: 'almaty@mitm.institute',
    lat: 43.240191,
    lon: 76.92528,
    zoom: 17
  },
  {
    type: 'tashkent' as const,
    city: 'Ташкент',
    address: 'ул. Афросиаб, 14, БЦ «Afrosiyob» (4 этаж)',
    addressExtra: null,
    phones: ['+998 (78) 122-62-26'],
    email: 'info@mitm.institute',
    lat: 41.299446,
    lon: 69.271921,
    zoom: 17
  }
]

export const ContactPage = ({
  categories,
  footer,
  popularPrograms
}: TContactPageProps) => {
  const { asPath } = useRouter()
  const [activeContact, setActiveContact] = useState<
    'moscow' | 'almaty' | 'tashkent'
  >('moscow')
  const [isFormOpen, setIsFormOpen] = useState(false)

  const items = ALL_ITEMS.filter(item => item.type === activeContact)

  const canonical = `${PATHS.front.root.index}${asPath}`

  return (
    <>
      <NextSeo
        title='Контакты | Московский Институт Технологий и Управления'
        description='Контакты МИТМ — адреса, телефоны и email офисов в Москве, Алматы и Ташкенте.'
        canonical={canonical}
        openGraph={{
          url: canonical,
          title: 'Контакты | Московский Институт Технологий и Управления',
          description:
            'Контакты МИТМ — адреса, телефоны и email офисов в Москве, Алматы и Ташкенте.',
          images: [
            {
              url: `${PATHS.front.root.index}${PATHS.front.public.assets.imgs.icons.mitmLogo512.index}`,
              width: 512,
              height: 512,
              alt: 'Московский Институт Технологий и Управления',
              type: 'image/png'
            }
          ],
          site_name: 'Московский Институт Технологий и Управления'
        }}
      />

      <HeaderUpdated
        categories={categories}
        popularPrograms={popularPrograms}
      />

      <main>
        <Breadcrumbs items={[{ label: 'Контакты' }]} />

        <section className='pb-[100px]'>
          <div
            className={cn(
              'mx-auto px-[16px] box-border',
              'max-w-[672px]',
              'md:max-w-[832px]',
              '2lg:max-w-[1280px]'
            )}>
            <h1 className='text-[30px]/[100%] sm:text-[32px]/[100%] font-medium md:text-[42px]/[100%] mb-[32px] mt-0'>
              Контактная информация
            </h1>

            <ContactButtons
              contacts={['moscow', 'almaty', 'tashkent']}
              activeContact={activeContact}
              setActiveContact={setActiveContact}
            />

            <div className='flex flex-col gap-[80px] mt-[40px]'>
              {items.map((item, idx) => (
                <div
                  key={`${item.type}-${idx}`}
                  className='flex flex-col gap-[40px]'>
                  {/* Контактная строка */}
                  <div
                    className={cn(
                      'flex flex-col gap-[24px]',
                      'md:flex-row md:justify-between md:gap-[32px]'
                    )}>
                    {/* Город */}
                    <div className='md:flex-1'>
                      <h3 className='text-[20px] md:text-[48px] font-medium m-0'>
                        {item.city}
                      </h3>
                    </div>

                    {/* Адрес */}
                    <div className='flex flex-col gap-[8px] md:flex-1'>
                      <span className='text-[14px] font-medium leading-[120%] text-[var(--upsilon)]'>
                        Адрес
                      </span>
                      <span className='text-[14px] leading-[140%]'>
                        {item.address}
                        {item?.addressExtra && (
                          <>
                            <br />
                            {item?.addressExtra}
                          </>
                        )}
                      </span>
                    </div>

                    {/* Телефоны */}
                    <div className='flex flex-col gap-[8px] md:flex-1'>
                      <span className='text-[14px] font-medium leading-[120%] text-[var(--upsilon)]'>
                        Телефон
                      </span>
                      <div className='flex flex-col'>
                        {item.phones.map((phone, i) => (
                          <span key={i} className='text-[14px] leading-[140%]'>
                            {i > 0 && '/ '}
                            {phone}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Email */}
                    <div className='flex flex-col gap-[8px] md:flex-1'>
                      <span className='text-[14px] font-medium leading-[120%] text-[var(--upsilon)]'>
                        Email
                      </span>
                      <a
                        href={`mailto:${item.email}`}
                        className='text-[14px] leading-[140%] hover:underline'>
                        {item.email}
                      </a>
                    </div>
                  </div>
                  <YMaps
                    query={{
                      apikey: process.env.NEXT_PUBLIC_YANDEX_MAPS_API_KEY,
                      lang: 'ru_RU',
                      load: 'package.full'
                    }}>
                    {/* Карта */}
                    <Map
                      key={item.type}
                      state={{
                        center: [item.lat, item.lon],
                        zoom: item?.zoom
                      }}
                      modules={[
                        'control.ZoomControl',
                        'control.FullscreenControl'
                      ]}
                      className='rounded-[8px] block h-[400px]'>
                      <Placemark
                        key={`${item.type}-${item.lat}-${item.lon}`}
                        geometry={[item.lat, item.lon]}
                        options={{
                          preset: 'islands#icon',
                          iconColor: '#1143A7'
                        }}
                      />
                    </Map>
                  </YMaps>
                </div>
              ))}
            </div>

            {/* Форма + фото */}
            <div className={cn('grid md:grid-cols-2 gap-[33px] mt-[80px]')}>
              <div className='relative w-full'>
                <Image
                  width={300}
                  height={300}
                  src='/assets/imgs/contact-page/people-studying.jpg'
                  alt='Люди учатся'
                  className='w-full h-full object-cover rounded-[12px]'
                />
              </div>
              <div className='bg-[#f2f2f2] rounded-[16px] p-[16px] md:p-[36px]'>
                <p className='font-medium text-[24px] text-center mb-[24px]'>
                  Остались вопросы? Получите консультацию приемной комиссии
                </p>
                <LeadForm
                  className='w-full flex flex-col justify-between'
                  buttonText='Получить консультацию'
                  buttonWrap
                />
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer data={footer} />
    </>
  )
}
